import { useReadDashboardSharedState } from '@hopin-team/dashboard-shared-react';
import { QueryFunctionContext, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { useAuthentication } from '../../../hooks/use-authentication';
import { useAnalyticsContext } from '../../../providers/analytics-provider';
import { fetchAdminEventsByOrganizer } from '../../../services/endpoints';
import { EventQuery } from '../../../types';
import { buildQuery, formatDate } from '../../../utils';

export type TOrganizerStatus = 'active' | 'inviteSent' | 'deleted' | 'rejected';

export type TOrganizerSortKey =
  | 'name'
  | 'email'
  | 'events_hosted'
  | 'events_created'
  | 'recent_event_date'
  | 'seat_allocation_date'
  | 'status';

const DEFAULT_SORT_KEY: TOrganizerSortKey = 'events_hosted';
const DEFAULT_SORT_REVERSE = true;

export type TOrganizerEventTypeDistribution = {
  webinar: number;
  virtual: number;
  hybrid: number;
  onSite: number;
};

export type TOrganizerRow = {
  id: string;
  name: string;
  email: string;
  pictureUrl: string | null;
  status: TOrganizerStatus;
  seatAllocationDate: string | null;
  recentEventDate: string | null;
  eventsHosted: number;
  eventsCreated: number;
  eventTypeDistribution: TOrganizerEventTypeDistribution;
};

export type UseEventsByOrganizerReturn = {
  data: TOrganizerRow[];
  isLoading: boolean;
  isError: boolean;
  isRefetchError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchMore: () => void;
  sortKey: TOrganizerSortKey;
  reverse: boolean;
  onSort: (key: TOrganizerSortKey) => void;
};

const ORGANIZER_PAGE_SIZE = 20;

type TBackendOrganizerStatus =
  | 'active'
  | 'pending'
  | 'invite_sent'
  | 'cancelled'
  | 'rejected';

type IOrganizerEventBreakdown = {
  virtual?: number;
  webinar?: number;
  hybrid?: number;
  onsite?: number;
};

type IOrganizerResponse = {
  id?: string | null;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  status: string;
  seat_allocation_date?: string | null;
  picture_url?: string | null;
  events_hosted?: number | null;
  events_created?: number | null;
  recent_event_date?: string | null;
  event_breakdown?: IOrganizerEventBreakdown | null;
};

type IPaginationResponse = {
  page: number;
  totalCount: number;
  totalPages: number;
  perPage: number;
};

type IPaginationFields = {
  page?: number;
  total_pages?: number;
  totalPages?: number;
  total_count?: number;
  totalCount?: number;
  per_page?: number;
  perPage?: number;
};

type IEventsByOrganizerResponse = IPaginationFields & {
  results?: IOrganizerResponse[];
  eventsByOrganizer?: IOrganizerResponse[];
  events_by_organizer?: IOrganizerResponse[];
  organizers?: IOrganizerResponse[];
  data?: IOrganizerResponse[];
  pagination?: IPaginationFields;
};

const STATUS_MAP: Record<TBackendOrganizerStatus, TOrganizerStatus> = {
  active: 'active',
  pending: 'inviteSent',
  invite_sent: 'inviteSent',
  cancelled: 'deleted',
  rejected: 'rejected',
};
const DEFAULT_ORGANIZER_STATUS: TOrganizerStatus = 'active';

const DEFAULT_PAGINATION: IPaginationResponse = {
  page: 1,
  totalCount: 0,
  totalPages: 1,
  perPage: ORGANIZER_PAGE_SIZE,
};

export const mapOrganizerStatus = (status: string): TOrganizerStatus =>
  STATUS_MAP[status as TBackendOrganizerStatus] ?? DEFAULT_ORGANIZER_STATUS;

export const formatOrganizerDate = (value?: string | null) =>
  value ? formatDate(value) : null;

export const transformOrganizerRow = (
  organizer: IOrganizerResponse,
): TOrganizerRow => {
  const firstName = organizer.first_name?.trim() || '';
  const lastName = organizer.last_name?.trim() || '';
  const name = `${firstName} ${lastName}`.trim() || organizer.email;

  return {
    id: organizer.id || organizer.email,
    name,
    email: organizer.email,
    pictureUrl: organizer.picture_url?.trim() || null,
    status: mapOrganizerStatus(organizer.status),
    seatAllocationDate: formatOrganizerDate(organizer.seat_allocation_date),
    recentEventDate: formatOrganizerDate(organizer.recent_event_date),
    eventsHosted: organizer.events_hosted ?? 0,
    eventsCreated: organizer.events_created ?? 0,
    eventTypeDistribution: {
      webinar: organizer.event_breakdown?.webinar ?? 0,
      virtual: organizer.event_breakdown?.virtual ?? 0,
      hybrid: organizer.event_breakdown?.hybrid ?? 0,
      onSite: organizer.event_breakdown?.onsite ?? 0,
    },
  };
};

export const getOrganizerRows = (
  response?: IEventsByOrganizerResponse,
): TOrganizerRow[] => {
  const organizers =
    response?.results ||
    response?.eventsByOrganizer ||
    response?.events_by_organizer ||
    response?.organizers ||
    response?.data ||
    [];

  return organizers.map(transformOrganizerRow);
};

const normalizePaginationFields = (
  source?: IPaginationFields | null,
): IPaginationResponse | null => {
  if (!source || source.page == null) {
    return null;
  }

  return {
    page: source.page,
    totalPages: source.total_pages ?? source.totalPages ?? 1,
    totalCount: source.total_count ?? source.totalCount ?? 0,
    perPage: source.per_page ?? source.perPage ?? ORGANIZER_PAGE_SIZE,
  };
};

export const getOrganizerPagination = (
  response?: IEventsByOrganizerResponse,
): IPaginationResponse => {
  const fromNested = normalizePaginationFields(response?.pagination);
  if (fromNested) {
    return fromNested;
  }

  const fromRoot = normalizePaginationFields(response);
  if (fromRoot) {
    return fromRoot;
  }

  return DEFAULT_PAGINATION;
};

export const useEventsByOrganizer = (): UseEventsByOrganizerReturn => {
  const { state } = useAnalyticsContext();
  const orgId: string = useReadDashboardSharedState('org_id') || '';
  const { token } = useAuthentication();
  const [sortKey, setSortKey] = useState<TOrganizerSortKey>(DEFAULT_SORT_KEY);
  const [reverse, setReverse] = useState(DEFAULT_SORT_REVERSE);

  const config = useMemo(
    () => ({
      dateRange: state.dateRange,
      startDate: state.startDate,
      endDate: state.endDate,
    }),
    [state.dateRange, state.startDate, state.endDate],
  );

  const handleSort = useCallback(
    (key: TOrganizerSortKey) => {
      if (key === sortKey) {
        setReverse(prev => !prev);
      } else {
        setSortKey(key);
        setReverse(true);
      }
    },
    [sortKey],
  );

  const requestEventsByOrganizer = useCallback(
    async ({
      pageParam = 1,
    }: QueryFunctionContext<
      [string, string, EventQuery, TOrganizerSortKey, boolean]
    >): Promise<IEventsByOrganizerResponse> => {
      const dateParams = buildQuery(config);
      const params = new URLSearchParams(dateParams);
      params.set('page', pageParam.toString());
      params.set('per_page', ORGANIZER_PAGE_SIZE.toString());
      params.set('sort_key', sortKey);
      params.set('reverse', String(reverse));

      return fetchAdminEventsByOrganizer(
        orgId,
        params.toString(),
      ) as Promise<IEventsByOrganizerResponse>;
    },
    [config, orgId, sortKey, reverse],
  );

  const queryState = useInfiniteQuery<
    IEventsByOrganizerResponse,
    unknown,
    IEventsByOrganizerResponse,
    [string, string, EventQuery, TOrganizerSortKey, boolean]
  >(
    ['admin-events-by-organizer', orgId, config, sortKey, reverse],
    requestEventsByOrganizer,
    {
      enabled: Boolean(token && orgId),
      getNextPageParam: lastPage => {
        const pagination = getOrganizerPagination(lastPage);

        if (pagination.page >= pagination.totalPages) {
          return undefined;
        }

        return pagination.page + 1;
      },
      retry: (failureCount, error) => {
        if ((error as any).response?.status === 401) {
          return false;
        }

        return failureCount < 3;
      },
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  );
  const { fetchNextPage, isLoading } = queryState;
  const isInitialLoadError = queryState.isLoadingError;
  const isRefetchError = queryState.isRefetchError;
  const hasNextPage = Boolean(queryState.hasNextPage);

  const fetchMore = useCallback(() => {
    if (!isLoading && hasNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isLoading]);

  const data = queryState.data?.pages.flatMap(getOrganizerRows) || [];

  return {
    data,
    isLoading: queryState.isLoading,
    isError: isInitialLoadError,
    isRefetchError,
    hasNextPage,
    isFetchingNextPage: queryState.isFetchingNextPage,
    fetchMore,
    sortKey,
    reverse,
    onSort: handleSort,
  };
};
